import { createElement, forwardRef, ForwardRefExoticComponent, RefAttributes, SVGProps } from 'react';

const defaultAttributes = {
  xmlns: 'http://www.w3.org/2000/svg',
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
};

export type IconSvgElement = readonly (readonly [string, { readonly [key: string]: string | number }])[];

export type SVGAttributes = Partial<SVGProps<SVGSVGElement>>;
type ComponentAttributes = RefAttributes<SVGSVGElement> & SVGAttributes;

export interface HugeiconsProps extends ComponentAttributes {
  size?: string | number;
  strokeWidth?: number;
  absoluteStrokeWidth?: boolean;
  altIcon?: IconSvgElement;
  showAlt?: boolean;
  icon?: IconSvgElement;
}

export interface HugeiconsIconProps extends Omit<HugeiconsProps, 'ref' | 'altIcon'> {
  icon: IconSvgElement;
  altIcon?: IconSvgElement;
}

export type HugeiconsIcon = ForwardRefExoticComponent<HugeiconsProps>;

export const HugeiconsIcon = forwardRef<SVGSVGElement, HugeiconsIconProps>(
  (
    {
      color = 'currentColor',
      size = 24,
      strokeWidth,
      absoluteStrokeWidth = false,
      className = '',
      altIcon,
      showAlt = false,
      icon,
      ...rest
    },
    ref,
  ) => {
    const calculatedStrokeWidth = strokeWidth !== undefined
      ? (absoluteStrokeWidth ? (Number(strokeWidth) * 24) / Number(size) : strokeWidth)
      : undefined;

    const strokeProps: { stroke?: string; strokeWidth?: number } = calculatedStrokeWidth !== undefined ? {
      strokeWidth: calculatedStrokeWidth,
      stroke: 'currentColor'
    } : {};

    const elementProps = {
      ref,
      ...defaultAttributes,
      width: size,
      height: size,
      color,
      className,
      ...strokeProps,
      ...rest,
    };

    const currentIcon = (showAlt && altIcon) ? altIcon : icon;

    // Create SVG children without adding them as an icon prop
    const svgChildren = currentIcon.map(([tag, attrs]) => {
      return createElement(tag, { 
        ...attrs, 
        ...strokeProps,
        key: attrs.key 
      });
    });

    return createElement(
      'svg',
      elementProps,
      svgChildren
    );
  },
);

HugeiconsIcon.displayName = 'HugeiconsIcon';

export default HugeiconsIcon; 